import React, { useState } from "react";
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaXTwitter,
} from "react-icons/fa6";
import { ChevronRight } from "lucide-react";

/* ─── Data ──────────────────────────────────────────────────────── */
const solutions = [
  "AI Smile Simulation for Dental Clinics",
  "Cosmetic Dentistry Smile Simulation",
  "Orthodontic Smile Preview",
  "DSO Smile Simulation Platform",
  "Multi-Location Dental Practice Solution",
];

const resources = [
  { label: "ROI Calculator", href: "/roi-calculator" },
  { label: "HIPAA Compliance", href: "/hipaa" },
  { label: "SmileReveal vs PreVu", href: "/compare-prevu" },
  { label: "SmileReveal vs SmileFy", href: "/compare-smilefy" },
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Terms of Service", href: "/tos" },
];

const socials = [
  { Icon: FaFacebookF, href: "#", label: "Facebook" },
  { Icon: FaInstagram, href: "#", label: "Instagram" },
  { Icon: FaLinkedinIn, href: "#", label: "LinkedIn" },
  { Icon: FaXTwitter, href: "#", label: "Twitter / X" },
];

/* ─── Contact icon helper ───────────────────────────────────────── */
const PhoneIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 11.9a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.8 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const PersonIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const MailIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect width="20" height="16" x="2" y="4" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

/* ─── Newsletter Form ───────────────────────────────────────────── */
const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setStatus("error");
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    setStatus("loading");
    try {
      await new Promise((res) => setTimeout(res, 800));
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
      setErrorMsg("Something went wrong. Please try again.");
    }
  };

  const isDisabled = status === "loading" || status === "success";

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col sm:flex-row items-start gap-3 w-full"
    >
      <div className="flex-1 w-full">
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (status === "error") setStatus("idle");
          }}
          placeholder="Enter your email"
          disabled={isDisabled}
          className={`w-full rounded-full px-5 py-3 bg-[#1a2744]/60 border text-white placeholder-slate-400 text-sm outline-none transition-all duration-200 focus:border-brand/60 focus:ring-2 focus:ring-brand/20 disabled:opacity-50 ${status === "error"
            ? "border-red-500/70 ring-2 ring-red-500/20"
            : "border-[#2a3a5c]"
            }`}
        />
        {status === "error" && (
          <p className="text-red-400 text-xs mt-1.5 pl-4">{errorMsg}</p>
        )}
      </div>

      <button
        type="submit"
        disabled={isDisabled}
        className={`group flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold whitespace-nowrap transition-all duration-200 disabled:opacity-70 ${status === "success"
          ? "bg-emerald-500 text-white"
          : "bg-white text-gray-900 hover:bg-gray-100"
          }`}
      >
        {status === "loading" && (
          <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8H4z"
            />
          </svg>
        )}
        {status === "success" ? (
          "You're in! ✓"
        ) : status === "loading" ? (
          "Sending…"
        ) : (
          <>
            Get Started
            <div className="flex items-center text-current">
              <ChevronRight className="h-4 w-4 opacity-40 group-hover:translate-x-0.5 transition-transform" />
              <ChevronRight className="h-5 w-5 opacity-70 -ml-2.5 group-hover:translate-x-0.5 transition-transform delay-75" />
              <ChevronRight className="h-6 w-6 opacity-100 -ml-3.5 group-hover:translate-x-0.5 transition-transform delay-150" />
            </div>
          </>
        )}
      </button>
    </form>
  );
};

/* ─── Footer ────────────────────────────────────────────────────── */
const Footer = () => {
  return (
    <footer className="bg-[#021325] text-white">
      {/* ── Newsletter Band ── */}
      <div className="max-w-7xl mx-auto px-4 lg:px-6 pt-16 pb-10">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-16">
          {/* Left: copy */}
          <div className="lg:max-w-xs">
            <h3 className="text-2xl font-bold text-white leading-snug">
              Dental Practice Growth Newsletter
            </h3>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">
              Get AI smile simulation tips, dental case acceptance strategies,
              and implant marketing insights—straight to your inbox each month.
              Join 2,000+ dental professionals.
            </p>
          </div>

          {/* Right: form */}
          <div className="flex-1">
            <NewsletterForm />
          </div>
        </div>
      </div>

      {/* ── Divider ── */}
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <hr className="border-slate-700/50" />
      </div>

      {/* ── Footer Grid ── */}
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[2fr_1.6fr_1.2fr_1.4fr] gap-10 lg:gap-6">
          {/* Col 1: Brand */}
          <div className="flex flex-col gap-4">
            <img
              src="/logo_transparent.webp"
              alt="SmileReveal"
              width="160"
              height="40"
              loading="lazy"
              className="h-10 w-auto object-contain object-left brightness-0 invert"
            />
            <p className="text-sm text-slate-400 leading-relaxed max-w-[220px]">
              AI smile simulation software that boosts dental case acceptance to
              60%+
            </p>

            {/* Social Icons */}
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest mr-1">
                Follow Us:
              </span>
              {socials.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  className="w-9 h-9 rounded-full flex items-center justify-center bg-[#1a2a45] border border-[#253550] text-slate-400 hover:bg-brand hover:border-brand hover:text-white transition-all duration-200"
                >
                  <Icon size={14} />
                </a>
              ))}
            </div>
          </div>

          {/* Col 2: Solutions */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-5 tracking-wide">
              Solutions
            </h4>
            <ul className="flex flex-col gap-3">
              {solutions.map((label) => (
                <li key={label} className="text-sm text-slate-400">
                  {label}
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4: Resources */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-5 tracking-wide">
              Resources
            </h4>
            <ul className="flex flex-col gap-3">
              {resources.map(({ label, href }) => (
                <li key={label}>
                  <a
                    href={href}
                    className="text-sm text-slate-400 hover:text-white transition-colors duration-150"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 5: Contact */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-5 tracking-wide">
              Contact
            </h4>
            <ul className="flex flex-col gap-4">
              <li>
                <a
                  href="tel:+19714318339"
                  className="flex items-start gap-3 text-sm text-slate-400 hover:text-white transition-colors duration-150"
                >
                  <span className="mt-0.5 w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center bg-[#1a2a45] border border-[#253550] text-slate-400">
                    <PhoneIcon />
                  </span>
                  +1 971-431-8339
                </a>
              </li>
              <li>
                <a
                  href="mailto:sales@mail.smilereveal.io"
                  className="flex items-start gap-3 text-sm text-slate-400 hover:text-white transition-colors duration-150"
                >
                  <span className="mt-0.5 w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center bg-[#1a2a45] border border-[#253550] text-slate-400">
                    <PersonIcon />
                  </span>
                  sales@mail.smilereveal.io
                </a>
              </li>
              <li>
                <a
                  href="mailto:support@mail.smilereveal.io"
                  className="flex items-start gap-3 text-sm text-slate-400 hover:text-white transition-colors duration-150"
                >
                  <span className="mt-0.5 w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center bg-[#1a2a45] border border-[#253550] text-slate-400">
                    <MailIcon />
                  </span>
                  support@mail.smilereveal.io
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* ── Bottom Bar ── */}
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <hr className="border-slate-700/50" />
      </div>
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} SmileReveal. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <a
              href="/"
              className="hover:text-slate-300 transition-colors duration-150"
            >
              Imprint
            </a>
            <span className="text-slate-700">|</span>
            <a
              href="/"
              className="hover:text-slate-300 transition-colors duration-150"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
